import { Col, Form, Row, Skeleton, Spin } from "antd";
import { FormComponentProps } from "antd/lib/form";
import * as React from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { Redirect, RouteComponentProps, withRouter } from "react-router-dom";
import { SendQuoteModal } from 'src/components/common/Modais/SendQuoteModal/SendQuoteModal';
import { PbrCard } from "src/components/common/PbrCard/PbrCard";
import { PricingResult } from "src/components/common/pricing-result/PricingResult";
import { ShowNotification } from 'src/components/common/ShowNotification/ShowNotification';
import StandardButton from "src/components/common/StandardButton/StandardButton";
import { AuthContext } from 'src/components/context/AuthContext';

import { IPricingCalculatorResponse } from "src/services/interfaces/calculator/IPricingCalculatorResponse";
import { ITrade } from "src/services/interfaces/trade/ITrade";
import { IQuote } from "src/services/interfaces/trade/precificacao/IQuote";
import { IQuoteUpdateRequest } from "src/services/interfaces/trade/precificacao/IQuoteUpdateRequest";
import { QuoteStatusEnum } from 'src/services/interfaces/trade/precificacao/QuoteStatusEnum';
import { MapQuote } from "src/services/mapper/quote.mapper";
import { ServiceContext } from "src/services/provider/Service.provider";
import "./quote.scss";
import QuoteEditComponent from "./QuoteEditComponent";
import { RejectPrePrecatorioButton } from 'src/components/Refatoracao/RejectPrePrecatorio/RejectPrePrecatorioButton';

function QuoteEditPage(props: { style?: React.CSSProperties; pageSize?: number; max?: number } & RouteComponentProps<any> & FormComponentProps) {
  const { tradeService, quoteService, quotePricingService } = useContext(ServiceContext);
  const { currentUser } = useContext(AuthContext);

  const urlQuoteId = Number((props.match.params as any).quoteId);
  const [loading, setLoading] = useState(false);
  const [sendQuoteModalVisible, setSendQuoteModalVisible] = useState(false);
  const [currentQuote, setCurrentQuote] = useState<IQuote>(null);
  const [currentQuotePricingResponse, setCurrentQuotePricingResponse] = useState<IPricingCalculatorResponse>(null);
  const [newQuotePricingResponse, setNewQuotePricingResponse] = useState<IPricingCalculatorResponse>(null);
  const [isVendaDisabled, setIsVendaDisabled] = useState(false);
  const [confirmQuoteDisabled, setConfirmQuoteDisabled] = useState(false);
  const [canEditQuote, setCanEditQuote] = useState(true);
  const [currentTrade, setCurrentTrade] = useState<ITrade>(null);

  useMemo(() => {
    if (urlQuoteId) {
      getQuoteById(urlQuoteId);
    }
  }, []);

  useEffect(() => {
    setIsVendaDisabled(currentQuote?.status === QuoteStatusEnum.Aceito);
  }, [currentQuote?.status]);

  async function getQuoteById(id: number) {
    setLoading(true);

    quoteService.getById(id).then(res => {
      res.precatorio.isPss = res.pricing.isPss;
      setCurrentQuote(res);
      setCurrentQuotePricingResponse(MapQuote.ToPricingCalculatorResponse(res));
      setIsVendaDisabled(false);
    }).catch(err => {
      const errorQuote = err?.response?.data?.quote;
      if (errorQuote) {
        if (errorQuote.status === QuoteStatusEnum.Pendente) {
          setCurrentQuote(errorQuote);
          setSendQuoteModalVisible(true);
          setCanEditQuote(false);
        }
      }
      else {
        ShowNotification.Error("Falha ao exibir a cotação", err?.response?.data?.message);
        props.history.push(`/monitor/cotacoes/user`);
      }
    })
      .finally(() => setLoading(false));
  }

  async function confirmQuote() {
    setLoading(true);
    setConfirmQuoteDisabled(true);

    try {
      let savedPricingResponse = null;
      if (newQuotePricingResponse) {
        savedPricingResponse = await quotePricingService.savePricing(newQuotePricingResponse, currentQuote.id);
      }

      const request: IQuoteUpdateRequest = {
        idPricing: savedPricingResponse?.id ?? currentQuote.pricing.id,
        idQuote: currentQuote.id,
        notifyDecode: !currentUser.permissions.canCreateTradeFromQuote
      }
      try {
        const res = await quoteService.postUpdateQuoteRecalculated(request);
        setCurrentQuotePricingResponse(MapQuote.ToPricingCalculatorResponse(res));
        setNewQuotePricingResponse(null);
        setCurrentQuote(res);
        setIsVendaDisabled(false);
        if (!currentUser.permissions.canCreateTradeFromQuote) {
          setSendQuoteModalVisible(true);
        }
      } catch (error) {
        setIsVendaDisabled(true);
        setConfirmQuoteDisabled(false);
        ShowNotification.Error("Falha no recálculo", "Ocorreu um erro durante o recálculo. Tente novamente.")
      }

      setLoading(false);

    } finally {
      setLoading(false);
    }

  }

  async function acceptAndStartQuote() {
    setIsVendaDisabled(true);
    setLoading(true);

    tradeService
      .postCreateFromQuote(currentQuote)
      .then(result => {
        setCurrentTrade(result);
      })
      .catch(() => {
        setIsVendaDisabled(false);
        ShowNotification.Error("Erro durante o processo de venda.", "Não foi possível prosseguir com o processo de venda. Por favor tente novamente, ou entre em contato conosco.")
      });
  }

  return (
    <div className="quote">
      {currentTrade && <Redirect to={`/venda/${currentTrade.id}`} />}

      {/* The code above was created for Redirect the page when one user try to
      return to the previous page and it generates new trades in one precatorio*/}

      <Spin spinning={loading}>
        <PbrCard title={`Editando Cotação: ${urlQuoteId}`} bordered={false} style={{ width: "100%" }}
          showLinkButton={true} linkButtonIcon={"arrow-left"} linkButtonTitle={"Voltar"} linkButtonUrl={"/monitor/cotacoes/user"}>
          <Form>
            {currentQuote && canEditQuote && (
              <div className="editLayout">
                <Row gutter={32}>
                  <Col xs={24} sm={24} md={8} lg={8}>
                    <PricingResult title={"Cotação atual"} result={currentQuotePricingResponse} precatorio={currentQuote.precatorio} showAllData={true} />
                  </Col>
                  <Col xs={24} sm={24} md={8} lg={8}>
                    <div className="pricing-header">
                      <span className="subtitle">Alterar dados da cotação</span>
                    </div>
                    <QuoteEditComponent
                      setLoading={setLoading}
                      setIsVendaDisabled={setIsVendaDisabled}
                      currentQuote={currentQuote}
                      setCurrentQuote={setCurrentQuote}
                      setNewQuotePricingResponse={setNewQuotePricingResponse}
                      setConfirmQuoteDisabled={setConfirmQuoteDisabled}
                      form={props.form}
                      setCurrentQuotePricingResponse={setCurrentQuotePricingResponse}
                    />
                  </Col>
                  {(newQuotePricingResponse || (!newQuotePricingResponse && !currentUser.permissions.canCreateTradeFromQuote)) && !loading && (
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <div className="pricing-header">
                        <span className="subtitle">{!currentUser.permissions.canCreateTradeFromQuote ? "Negociação" : "Cotação"}</span>
                      </div>
                      {loading && (<><Skeleton /><Skeleton /></>)}

                      {newQuotePricingResponse && (<PricingResult title={"Valor final da cotação"} result={newQuotePricingResponse} precatorio={currentQuote.precatorio} showAllData={false} />)}

                      <Row style={{ textAlign: "center", marginTop: "30px" }}>
                        <StandardButton style={{ color: "white" }} onClick={confirmQuote} disabled={confirmQuoteDisabled && !newQuotePricingResponse}>
                          {!currentUser.permissions.canCreateTradeFromQuote ? "Prosseguir com negociação" : "Confirmar Cotação"}
                        </StandardButton>
                      </Row>
                    </Col>
                  )}

                  {!newQuotePricingResponse && currentUser.permissions.canCreateTradeFromQuote && !loading && (
                    <Col xs={24} sm={24} md={8} lg={8}>
                      <div className="pricing-header">
                        <span className="subtitle">Venda </span>
                      </div>
                      {loading && (<><Skeleton /><Skeleton /></>)}
                      {(
                        <Row style={{ textAlign: "center", marginTop: "30px" }}>
                          <Col xs={24} sm={24} md={24} lg={24} style={{ marginBottom: "15px" }}>
                            <StandardButton disabled={isVendaDisabled} style={{ color: "white" }} onClick={() => acceptAndStartQuote()}>
                              Iniciar Venda
                            </StandardButton>
                          </Col>
                          <Col xs={24} sm={24} md={24} lg={24} >
                            <StandardButton style={{ color: "white" }}
                              onClick={() => props.history.push(`/monitor/precatorios/list/${currentQuote.precatorio.id}`)}>
                              Editar Precatório
                            </StandardButton>
                          </Col>
                          {currentQuote.idPrePrecatorio &&
                            <Col xs={24} sm={24} md={24} lg={24} >
                              <RejectPrePrecatorioButton
                                className="pbr-std-btn pbr-std-btn-normal"
                                style={{ maxWidth: "324px", margin: "auto", marginTop: "15px" }}
                                idPrePrecatorio={currentQuote.idPrePrecatorio}
                                setLoading={setLoading}
                                onReject={() => props.history.push(`/monitor/cotacoes/user`)}
                                title="Deseja Rejeitar a Cotação? Informe qualquer detalhe adicional"
                              />
                            </Col>
                          }
                        </Row>)}
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </Form>
        </PbrCard>
        <SendQuoteModal visible={sendQuoteModalVisible} quote={currentQuote} onClose={() => {
          props.history.push(`/monitor/cotacoes/user`);
        }} />
      </Spin>
    </div >
  );
}

export default withRouter(Form.create()(QuoteEditPage));



// WEBPACK FOOTER //
// ./src/pages/monitor/my-quotes/QuoteEdit.page.tsx